import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, IconButton, Dialog,
    DialogTitle, DialogContent, DialogActions, Alert, CircularProgress,
    Card, CardContent, Divider, Stack
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { FaPlus, FaFileUpload, FaEdit, FaTrash, FaCheck, FaTimes, FaChartLine, FaDownload, FaChartBar, FaCalendarAlt } from 'react-icons/fa';
import { getBudget, saveBudget, uploadBudget, getBudgetVariance, getCompetences, downloadBudgetTemplate, deleteBudget, deleteBudgetItem, getThresholds, getConfig } from '../api/api';


const BudgetManagementPage = () => {
    const theme = useTheme();
    const [meses, setMeses] = useState([]);
    const [selectedMes, setSelectedMes] = useState('');
    const [budgetData, setBudgetData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [thresholds, setThresholds] = useState([]);

    // Modal e Form States
    const [openModal, setOpenModal] = useState(false);
    const [currentItem, setCurrentItem] = useState({ centro_custo: '', conta: '', valor_orcado: '' });
    const [ccOptions, setCcOptions] = useState([]);
    const [contaOptions, setContaOptions] = useState([]);

    // Upload State
    const [uploadFile, setUploadFile] = useState(null);

    useEffect(() => {
        loadMonths();
        loadThresholds();
        loadCCs();
        loadContas();
    }, []);

    const loadCCs = async () => {
        try {
            const res = await getConfig('centros_custo');
            if (res.data) setCcOptions(res.data.map(cc => cc.nome).sort());
        } catch (err) {
            console.error("Erro ao carregar CCs:", err);
        }
    };

    const loadContas = async () => {
        try {
            const res = await getConfig('plano_contas_data');
            if (res.data) setContaOptions(res.data);
        } catch (err) {
            console.error("Erro ao carregar Contas:", err);
        }
    };

    const loadThresholds = async () => {
        try {
            const res = await getThresholds();
            setThresholds(res.data);
        } catch (err) {
            console.error("Erro ao carregar limites:", err);
        }
    };

    useEffect(() => {
        if (selectedMes) {
            loadBudget();
        }
    }, [selectedMes]);

    const loadMonths = async () => {
        try {
            const response = await getCompetences(); // Changed from getDashboardMonths
            // getCompetences returns list of objects {id, mes, status, ...} or list of strings?
            // Let's assume it returns objects based on CompetenceManagementPage usage
            // We need to extract 'mes'
            const activeMonths = response.data.map(c => c.mes || c).sort();
            setMeses(activeMonths);

            if (activeMonths.length > 0) {
                // Set most recent as default
                setSelectedMes(activeMonths[activeMonths.length - 1]);
            }
        } catch (err) {
            console.error("Erro ao carregar meses:", err);
        }
    };

    const loadBudget = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await getBudget(selectedMes);
            setBudgetData(response.data);
        } catch (err) {
            setError('Erro ao carregar orçamento.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };


    const handleSaveItem = async () => {
        if (!currentItem.centro_custo || !currentItem.conta || !currentItem.valor_orcado) {
            alert("Preencha todos os campos");
            return;
        }

        try {
            await saveBudget({
                competencia: selectedMes,
                centro_custo: currentItem.centro_custo,
                conta: currentItem.conta,
                valor_orcado: parseFloat(currentItem.valor_orcado)
            });
            setSuccess('Item salvo com sucesso!');
            setOpenModal(false);
            loadBudget();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Erro ao salvar item.');
        }
    };

    const handleUpload = async () => {
        if (!uploadFile) return;

        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('competencia', selectedMes);

        setLoading(true);
        try {
            const response = await uploadBudget(formData);
            setSuccess(response.data.message);
            setUploadFile(null);
            loadBudget();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Erro no upload: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
    };

    return (
        <Box sx={{ p: 3, backgroundColor: 'background.default', minHeight: '100vh' }} className="page-fade-in">
            <Typography variant="h3" fontWeight="bold" gutterBottom className="premium-gradient-text" sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                <FaChartLine /> Controle Orçamentário
            </Typography>

            <Paper sx={{ p: 1, mb: 4, display: 'flex', alignItems: 'center', gap: 2, borderRadius: 3 }} className="glassmorphism">
                <Box sx={{ display: 'flex', alignItems: 'center', px: 2, gap: 2, borderRight: 1, borderColor: 'divider', mr: 1 }}>
                    <FaCalendarAlt color={theme.palette.primary.main} />
                    <TextField
                        select
                        label="Competência"
                        value={selectedMes}
                        onChange={(e) => setSelectedMes(e.target.value)}
                        SelectProps={{ native: true }}
                        variant="standard"
                        size="small"
                        sx={{ minWidth: 150, '& .MuiInput-underline:before': { borderBottom: 'none' }, '& .MuiInput-underline:after': { borderBottom: 'none' } }}
                    >
                        <option value="" disabled>Selecione</option>
                        {meses.map(m => <option key={m} value={m}>{m}</option>)}
                    </TextField>
                </Box>


            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            {loading ? <CircularProgress /> : (
                <>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={8}>
                            <Paper sx={{ p: 0, borderRadius: 4, overflow: 'hidden' }} className="glassmorphism">
                                <Box display="flex" justifyContent="space-between" alignItems="center" p={3} bgcolor="rgba(0,0,0,0.02)" borderBottom="1px solid var(--border-color)">
                                    <Typography variant="h6" fontWeight="bold">Itens Orçados</Typography>
                                    <Box display="flex" gap={2}>
                                        {budgetData.length > 0 && (
                                            <Button
                                                variant="text"
                                                color="error"
                                                startIcon={<FaTrash />}
                                                sx={{ borderRadius: 2 }}
                                                onClick={() => {
                                                    if (window.confirm(`Tem certeza que deseja excluir TODO o orçamento de ${selectedMes}?`)) {
                                                        setLoading(true);
                                                        deleteBudget(selectedMes)
                                                            .then(() => {
                                                                setSuccess('Orçamento excluído com sucesso');
                                                                loadBudget();
                                                            })
                                                            .catch(err => setError('Erro ao excluir orçamento: ' + err.message))
                                                            .finally(() => setLoading(false));
                                                    }
                                                }}
                                            >
                                                Excluir Tudo
                                            </Button>
                                        )}
                                        <Button
                                            startIcon={<FaPlus />}
                                            variant="contained"
                                            sx={{
                                                borderRadius: 2,
                                                background: 'var(--primary-gradient)',
                                                boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)'
                                            }}
                                            onClick={() => {
                                                setCurrentItem({ centro_custo: '', conta: '', valor_orcado: '' });
                                                setOpenModal(true);
                                            }}
                                        >
                                            Novo Item
                                        </Button>
                                    </Box>
                                </Box>
                                <Box p={2}>
                                    <TableContainer sx={{ maxHeight: 600, borderRadius: 2, border: '1px solid var(--border-color)' }}>
                                        <Table stickyHeader size="small">
                                            <TableHead>
                                                <TableRow sx={{ '& th': { bgcolor: 'rgba(0,0,0,0.03)', fontWeight: 'bold' } }}>
                                                    <TableCell>Centro de Custo</TableCell>
                                                    <TableCell>Conta</TableCell>
                                                    <TableCell align="right">Valor Orçado</TableCell>
                                                    <TableCell align="center">Ações</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {budgetData.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                                                            <Typography variant="body1" color="textSecondary">Nenhum dado encontrado para este mês.</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : budgetData.map((row, index) => (
                                                    <TableRow key={index} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                        <TableCell sx={{ fontWeight: 500 }}>{row.centro_custo}</TableCell>
                                                        <TableCell>{row.conta}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{formatCurrency(row.valor_orcado)}</TableCell>
                                                        <TableCell align="center">
                                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                                <IconButton size="small" onClick={() => {
                                                                    setCurrentItem(row);
                                                                    setOpenModal(true);
                                                                }} sx={{ color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.05) }} title="Editar">
                                                                    <FaEdit size={14} />
                                                                </IconButton>
                                                                <IconButton size="small" onClick={() => {
                                                                    if (window.confirm(`Excluir item: ${row.centro_custo} - ${row.conta}?`)) {
                                                                        setLoading(true);
                                                                        deleteBudgetItem(selectedMes, row.centro_custo, row.conta)
                                                                            .then(() => {
                                                                                setSuccess('Item excluído');
                                                                                loadBudget();
                                                                            })
                                                                            .catch(err => setError('Erro ao excluir item: ' + err.message))
                                                                            .finally(() => setLoading(false));
                                                                    }
                                                                }} sx={{ color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.05) }} title="Excluir">
                                                                    <FaTrash size={14} />
                                                                </IconButton>
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Card className="glassmorphism sach-card" sx={{ borderRadius: 4, border: '1px solid var(--border-color)' }}>
                                <CardContent sx={{ p: 4 }}>
                                    <Box display="flex" alignItems="center" gap={2} mb={3}>
                                        <Box sx={{ p: 1.5, borderRadius: 2, background: alpha(theme.palette.secondary.main, 0.1), color: theme.palette.secondary.main }}>
                                            <FaFileUpload size={24} />
                                        </Box>
                                        <Typography variant="h6" fontWeight="bold">Importar Dados</Typography>
                                    </Box>

                                    <Typography variant="body2" color="textSecondary" paragraph sx={{ mb: 3 }}>
                                        Carregue seu planejamento financeiro via arquivo CSV. O arquivo deve conter:
                                        <Box component="span" sx={{ display: 'block', mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1, fontFamily: 'monospace' }}>
                                            Centro de Custo; Conta; Valor
                                        </Box>
                                    </Typography>

                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        startIcon={<FaDownload />}
                                        sx={{ mb: 4, py: 1, borderRadius: 2, textTransform: 'none' }}
                                        onClick={() => {
                                            downloadBudgetTemplate().then(response => {
                                                const url = window.URL.createObjectURL(new Blob([response.data]));
                                                const link = document.createElement('a');
                                                link.href = url;
                                                link.setAttribute('download', 'orcamento_modelo.csv');
                                                document.body.appendChild(link);
                                                link.click();
                                                link.remove();
                                            });
                                        }}
                                    >
                                        Baixar Modelo de Importação
                                    </Button>

                                    <Divider sx={{ mb: 4 }} />

                                    <Box
                                        sx={{
                                            p: 3,
                                            border: '2px dashed var(--border-color)',
                                            borderRadius: 3,
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            '&:hover': { bgcolor: 'action.hover', borderColor: theme.palette.primary.main }
                                        }}
                                        onClick={() => document.getElementById('raised-button-file').click()}
                                    >
                                        <input
                                            accept=".csv"
                                            style={{ display: 'none' }}
                                            id="raised-button-file"
                                            type="file"
                                            onChange={(e) => setUploadFile(e.target.files[0])}
                                        />
                                        <Typography variant="body2" color={uploadFile ? 'text.primary' : 'textSecondary'} fontWeight={uploadFile ? 'bold' : 'normal'}>
                                            {uploadFile ? uploadFile.name : 'Clique para selecionar o arquivo'}
                                        </Typography>
                                    </Box>

                                    <Button
                                        variant="contained"
                                        fullWidth
                                        sx={{
                                            mt: 3,
                                            py: 1.5,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 'bold',
                                            background: 'var(--primary-gradient)'
                                        }}
                                        disabled={!uploadFile || !selectedMes}
                                        onClick={handleUpload}
                                    >
                                        Iniciar Importação
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </>
            )}

            {/* Modal de Adição/Edição */}
            <Dialog
                open={openModal}
                onClose={() => setOpenModal(false)}
                PaperProps={{
                    sx: { borderRadius: 3, p: 1, width: '100%', maxWidth: 450 }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography variant="h5" fontWeight="bold" className="premium-gradient-text">
                        {currentItem.id ? 'Editar Item' : 'Novo Planejamento'}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Preencha os dados abaixo para definir o orçamento planejado.
                    </Typography>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            select
                            label="Centro de Custo"
                            fullWidth
                            variant="outlined"
                            value={currentItem.centro_custo}
                            onChange={(e) => setCurrentItem({ ...currentItem, centro_custo: e.target.value })}
                            SelectProps={{ native: true }}
                        >
                            <option value="" disabled>Selecione...</option>
                            {ccOptions.map(cc => <option key={cc} value={cc}>{cc}</option>)}
                        </TextField>
                        <TextField
                            select
                            label="Conta Contábil"
                            fullWidth
                            variant="outlined"
                            value={currentItem.conta}
                            onChange={(e) => setCurrentItem({ ...currentItem, conta: e.target.value })}
                            SelectProps={{ native: true }}
                        >
                            <option value="" disabled>Selecione...</option>
                            {contaOptions.map((acc, index) => (
                                <option key={index} value={acc.Conta}>
                                    {acc.Conta} - {acc.Descricao}
                                </option>
                            ))}
                        </TextField>
                        <TextField
                            label="Valor Planejado"
                            type="number"
                            fullWidth
                            variant="outlined"
                            value={currentItem.valor_orcado}
                            onChange={(e) => setCurrentItem({ ...currentItem, valor_orcado: e.target.value })}
                            InputProps={{
                                startAdornment: <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>R$</Typography>
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={() => setOpenModal(false)} sx={{ borderRadius: 2, textTransform: 'none' }}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSaveItem}
                        variant="contained"
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            textTransform: 'none',
                            fontWeight: 'bold',
                            background: 'var(--primary-gradient)'
                        }}
                    >
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BudgetManagementPage;
